import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';
import { getRecentlyViewed, clearRecentlyViewed } from '../../utils/recentlyViewed';
import { getMarketplaceItemDetailPath, getListingLinkState } from '../../utils/itemLinks';
import { formatListingPriceLabel } from '../../utils/formatPrice';
import { resolveListingBrand } from './ListingDetailShared';

// Light-touch personalization. Reads from localStorage on mount and renders
// a horizontal strip of the user's last-viewed items. Zero backend cost.

const Section = styled.section`
  margin: 0 0 0.15rem;
  padding: 0.1rem 0;
  background: transparent;
  border: none;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.3rem;
`;

const Title = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ClearLink = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;

  &:hover {
    color: var(--primary-color);
    background-color: rgba(102, 126, 234, 0.08);
  }
`;

const Strip = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.35rem;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Card = styled(Link)`
  flex: 0 0 140px;
  scroll-snap-align: start;
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const Thumb = styled.div`
  width: 140px;
  aspect-ratio: 1;
  border-radius: 0;
  background: var(--bg-secondary);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: var(--text-light);
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  transform: scale(1.1);
`;

const Price = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const Meta = styled.div`
  font-size: 0.72rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
`;

const MetaBrand = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const RecentlyViewedStrip = ({ scope = 'regular_items', includeCategory }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const refresh = () => {
      const entries = getRecentlyViewed(scope);
      setItems(
        typeof includeCategory === 'function'
          ? entries.filter((entry) => includeCategory(entry.category))
          : entries,
      );
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [scope, includeCategory]);

  if (items.length === 0) return null;

  return (
    <Section aria-label="Recently viewed items">
      <Header>
        <Title>🕒 Recently Viewed</Title>
        <ClearLink
          type="button"
          onClick={() => {
            clearRecentlyViewed(scope);
            setItems([]);
          }}
        >
          Clear
        </ClearLink>
      </Header>
      <Strip>
        {items.map((item) => (
          <Card
            key={item.id}
            to={getMarketplaceItemDetailPath(item)}
            state={getListingLinkState(item)}
          >
            <Thumb>
              {item.primary_image ? (
                <ThumbImg src={item.primary_image} alt="" loading="lazy" decoding="async" />
              ) : (
                '📦'
              )}
            </Thumb>
            <Price>{formatListingPriceLabel(item)}</Price>
            <Meta>
              <MetaBrand>{resolveListingBrand(item) || '—'}</MetaBrand>
              {item.condition ? <span>· {item.condition}</span> : null}
            </Meta>
          </Card>
        ))}
      </Strip>
    </Section>
  );
};

export default RecentlyViewedStrip;
